import { memo, useMemo, useState, type ReactNode } from 'react';
import { diagnosticExplanation, diagnosticTitle, diagnosticValuesHint } from '../diagnostics/catalog';
import { dedupMessages, hasValues, type DiagnosticGroup, type SizeSet } from '../diagnostics/group';
import { diagnosticMessage, displayLabel, fixKindCode, fixNote, type Localized } from '../engine/localize';
import type { CompileResult, Diagnostic, Fix } from '../engine/types';
import { formatNumber, listParts, templateParts, useI18n } from '../i18n';
import { calcLines, countAfterFix, type CalcLine } from '../lib/findings';
import type { ReportKind } from '../lib/report';
import { sizeName, sizesLabel } from '../lib/sizes';
import { summarizeChanges } from '../lib/tokenDiff';
import { Icon, SeverityIcon } from '../ui/Icon';
import { BeforeAfter, ChangeSummary, SourceEdit } from './FixPreview';
import { Menu } from './Menu';

export interface FindingActions {
  onJump: (line: number) => void;
  onApply: (fix: Fix) => void;
  onReport: (group: DiagnosticGroup, kind: ReportKind) => void;
}

/** Engine text in the UI language; marked lang="fr" when an older engine only speaks French. */
function EngineText({ value, className = '' }: { value: Localized; className?: string }) {
  const { lang } = useI18n();
  return (
    <span className={className} lang={value.lang !== lang ? value.lang : undefined}>
      {value.text}
    </span>
  );
}

export function SizeBadges({ sizes, result }: { sizes: SizeSet; result: CompileResult }) {
  const { t, lang } = useI18n();
  if (result.document.sizes.length <= 1) return null;
  if (sizes === 'all') {
    return <span className="whitespace-nowrap rounded-pill bg-sunken px-2 py-0.5 text-2xs font-semibold text-muted">{t('diag.allSizes')}</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {sizes.map((s) => (
        <span key={s} className="whitespace-nowrap rounded-pill bg-heather/[0.14] px-2 py-0.5 text-2xs font-bold text-fg">
          {sizeName(result, s, lang)}
        </span>
      ))}
    </span>
  );
}

/** A role="switch" toggle with its label. */
export function Switch({
  checked,
  onChange,
  label,
  testId,
  className = '',
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  testId?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`group inline-flex items-center gap-2 whitespace-nowrap rounded-pill py-1 text-xs font-semibold text-muted hover:text-fg aria-checked:text-fg ${className}`}
    >
      <span
        aria-hidden="true"
        className="relative inline-flex h-4 w-7 shrink-0 items-center rounded-pill bg-line-strong transition-colors group-aria-checked:bg-accent motion-reduce:transition-none"
      >
        <span className="absolute left-0.5 h-3 w-3 rounded-pill bg-surface transition-transform group-aria-checked:translate-x-3 motion-reduce:transition-none" />
      </span>
      {label}
    </button>
  );
}

/**
 * One finding as a small story: what (catalogue title), where (row, line, sizes), why (plain
 * explanation), the shown computation, the line as written, then the correction to preview and apply.
 */
export const FindingCard = memo(function FindingCard({
  group,
  result,
  fixes,
  sourceLine,
  dense = false,
  actions,
}: {
  group: DiagnosticGroup;
  result: CompileResult;
  fixes: Fix[];
  sourceLine: string | null;
  dense?: boolean;
  actions: FindingActions;
}) {
  const { t, lang } = useI18n();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const preview = fixes.find((f) => f.id === previewId) ?? null;
  const title = diagnosticTitle(group.code, lang);
  const explanation = diagnosticExplanation(group.code, lang);
  const valuesHint = diagnosticValuesHint(group.code, lang);
  const isDoc = group.line_no < 1;
  const label = displayLabel(group.line_no, group.label, result, lang);
  const where = isDoc ? t('find.document') : t('diag.line', { n: group.line_no });
  const calcs = useMemo(() => calcLines(group, result), [group, result]);
  const messages = useMemo(
    () => dedupMessages(group.diagnostics.filter((d) => !hasValues(d)), result.document.sizes.length),
    [group.diagnostics, result.document.sizes.length],
  );
  const messageFor = (d: Diagnostic) => diagnosticMessage(d, lang);
  const multiSize = result.document.sizes.length > 1;
  const patternLang = result.document.lang;
  const sev = group.severity;
  const pad = dense ? 'px-4 py-3' : 'px-6 py-5';

  return (
    <article
      className={`print-break-avoid rounded-card bg-surface ${pad} ${dense ? 'border border-line' : ''}`}
      data-testid="diagnostic-card"
      data-code={group.code}
      data-line={group.line_no}
      data-severity={sev}
      aria-label={`${t(`sev.${sev}`)} · ${title} · ${where}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => !isDoc && actions.onJump(group.line_no)}
          disabled={isDoc}
          title={isDoc ? undefined : t('diag.goto', { n: group.line_no })}
          className="group -mx-1 flex min-w-0 flex-1 items-start gap-2 rounded-control px-1 text-left"
          data-testid="diagnostic-jump"
        >
          <SeverityIcon severity={sev} size={dense ? 14 : 16} className={dense ? 'mt-[3px]' : 'mt-[5px]'} />
          <span className="min-w-0 flex-1">
            <span className={`font-display block leading-snug text-fg ${dense ? 'text-md font-[520]' : 'text-lg font-[480]'}`}>
              {title}
              <span className="sr-only"> ({t(`sev.${sev}`)})</span>
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted">
              {label && (
                <span className="font-semibold text-fg" data-verbatim={label.quoted || undefined} lang={label.quoted ? patternLang : undefined}>
                  {label.text}
                </span>
              )}
              {label && <span aria-hidden="true">·</span>}
              <span className="inline-flex items-center gap-1 group-enabled:group-hover:text-fg group-enabled:group-hover:underline">
                {where}
                {!isDoc && <Icon name="arrowRight" size={11} />}
              </span>
              {dense && <span className="font-mono text-2xs text-subtle">{group.code}</span>}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          <SizeBadges sizes={group.sizes} result={result} />
          <span data-print-hide>
            <Menu
              label={t('find.report')}
              icon="flag"
              className="btn-quiet h-7 w-7 justify-center px-0"
              testId="report-menu"
              items={[
                { id: 'false', label: t('find.reportFalse'), onSelect: () => actions.onReport(group, 'false-positive'), testId: 'report-false' },
                { id: 'misread', label: t('find.reportMisread'), onSelect: () => actions.onReport(group, 'misread'), testId: 'report-misread' },
              ]}
            />
          </span>
        </div>
      </div>

      <div className={dense ? 'mt-2 space-y-3 pl-6' : 'mt-3 space-y-4 pl-6'}>
        {explanation && (
          <p className="text-sm leading-relaxed text-muted" data-testid="diagnostic-explanation">
            {explanation}
          </p>
        )}

        {(calcs.length > 0 || messages.length > 0) && (
          <ul className="space-y-2" data-testid="diagnostic-facts">
            {calcs.map((c, i) => (
              <CalcFact
                key={`${c.size}-${i}`}
                line={c}
                result={result}
                title={[
                  ...group.diagnostics.filter((d) => d.size === c.size || d.size === null).map((d) => messageFor(d).text),
                  valuesHint,
                ]
                  .filter(Boolean)
                  .join('\n')}
                preview={preview}
                multiSize={multiSize}
              />
            ))}
            {messages.map((m) => {
              const d = group.diagnostics.find((x) => x.message === m.text)!;
              return (
                <li key={m.text} className="flex items-baseline gap-2 text-sm leading-relaxed text-fg">
                  {multiSize && (messages.length > 1 || calcs.length > 0) && <SizeBadges sizes={m.sizes} result={result} />}
                  <EngineText value={messageFor(d)} />
                </li>
              );
            })}
          </ul>
        )}

        {sourceLine !== null && !isDoc && (
          <figure
            className={`rounded-control bg-editor px-4 py-3 ${preview ? 'ring-1 ring-inset ring-accent/40' : ''}`}
            aria-label={preview ? t('find.previewAria', { n: group.line_no }) : t('diag.source')}
          >
            <figcaption className="kicker mb-1 flex items-center gap-2">
              <span>{preview ? t('find.previewAria', { n: group.line_no }) : t('diag.line', { n: group.line_no })}</span>
            </figcaption>
            {preview?.replacement ? (
              <SourceEdit oldText={preview.replacement.old_text} newText={preview.replacement.new_text} patternLang={patternLang} />
            ) : (
              <code className="verbatim block whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-fg" data-verbatim lang={patternLang}>
                {sourceLine}
              </code>
            )}
            {preview && !preview.replacement && (
              <div className="mt-2">
                <BeforeAfter before={preview.before} after={preview.after} t={t} />
              </div>
            )}
          </figure>
        )}

        {group.dependsOn.length > 0 && <DependsOn group={group} onJump={actions.onJump} />}

        {fixes.length > 0 && <FixList fixes={fixes} result={result} previewId={previewId} onPreview={setPreviewId} onApply={actions.onApply} dense={dense} />}

      </div>
    </article>
  );
});

/**
 * 1.6.0 (ADR-013, decision 6): « Dépend de la lecture par îlot de la ligne 18. », each line a link to the
 * manuscript, and for a warning lowered from an error, why it is only a warning for now.
 */
function DependsOn({ group, onJump }: { group: DiagnosticGroup; onJump: (line: number) => void }) {
  const { t, lang } = useI18n();
  const lines = group.dependsOn;
  const links = listParts(lang, lines.length).map((piece, i) => {
    if (typeof piece === 'string') return <span key={`s${i}`}>{piece}</span>;
    const n = lines[piece.item];
    return (
      <button
        key={n}
        type="button"
        onClick={() => onJump(n)}
        className="rounded font-semibold text-fg underline decoration-dotted underline-offset-2 hover:text-accent-strong"
        aria-label={t('diag.goto', { n })}
        data-testid="depends-on-line"
        data-line={n}
      >
        {n}
      </button>
    );
  });
  return (
    <div className="flex items-start gap-2 text-sm leading-relaxed text-muted" data-testid="diagnostic-depends-on">
      <span aria-hidden="true" className="mt-px select-none font-bold text-warning">
        ≈
      </span>
      <p>
        {templateParts(lang, lines.length === 1 ? 'find.dependsOn1' : 'find.dependsOnN').map((part, i) =>
          typeof part === 'string' ? <span key={i}>{part}</span> : <span key={i}>{links}</span>,
        )}
        {group.lowered && (
          <>
            {' '}
            <span data-testid="diagnostic-lowered">{t('find.lowered')}</span>
          </>
        )}
      </p>
    </div>
  );
}

/** "L  98 → 84 = 14 × 6   attendu 86 · calculé 84", and with a fix in preview, what it becomes. */
function CalcFact({
  line,
  result,
  title,
  preview,
  multiSize,
}: {
  line: CalcLine;
  result: CompileResult;
  title: string;
  preview: Fix | null;
  multiSize: boolean;
}) {
  const { t, lang } = useI18n();
  const valued = line.expected !== null || line.actual !== null;
  const after = preview ? countAfterFix(line, preview) : null;
  return (
    <li
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-fg"
      title={title}
      data-testid={valued ? 'diagnostic-values' : 'diagnostic-calc'}
    >
      {multiSize && <SizeBadges sizes={line.size === null ? 'all' : [line.size]} result={result} />}
      {line.calc && (
        <span className="tabular">
          <span className="kicker mr-2">{t('find.calc')}</span>
          <span className="font-semibold">{line.calc}</span>
        </span>
      )}
      {valued && (
        <span className="tabular text-muted">
          {line.expected !== null && (
            <>
              {t('diag.expected')} <strong className="font-semibold text-error">{formatNumber(lang, line.expected, 2)}</strong>
            </>
          )}
          {line.expected !== null && line.actual !== null && <span className="text-subtle"> · </span>}
          {line.actual !== null && (
            <>
              {t('diag.actual')} <strong className="font-semibold text-fg">{formatNumber(lang, line.actual, 2)}</strong>
            </>
          )}
        </span>
      )}
      {after !== null && (
        <span className="inline-flex items-center gap-1 tabular text-accent-strong" data-testid="diagnostic-after">
          <Icon name="arrowRight" size={11} />
          {t('find.afterFix')} {t('diag.expected')} <strong>{formatNumber(lang, after, 2)}</strong> · {t('diag.actual')}{' '}
          <strong>{formatNumber(lang, after, 2)}</strong>
          <Icon name="check" size={12} />
        </span>
      )}
    </li>
  );
}

/** The engine lists fixes cheapest first: the first is proposed, the others fold under "Autres pistes". */
function FixList({
  fixes,
  result,
  previewId,
  onPreview,
  onApply,
  dense,
}: {
  fixes: Fix[];
  result: CompileResult;
  previewId: string | null;
  onPreview: (id: string | null) => void;
  onApply: (fix: Fix) => void;
  dense: boolean;
}) {
  const { t } = useI18n();
  const [first, ...others] = fixes;
  return (
    <div className="space-y-3" data-testid="fix-list">
      <div className="kicker">{t('find.proposal')}</div>
      <FixOption fix={first} result={result} previewId={previewId} onPreview={onPreview} onApply={onApply} recommended dense={dense} />
      {others.length > 0 && (
        <details className="group" data-testid="fix-others">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-control py-1 text-xs font-semibold text-muted hover:text-fg [&::-webkit-details-marker]:hidden">
            <Icon name="arrow" size={11} className="transition-transform group-open:rotate-90 motion-reduce:transition-none" />
            {t('find.others', { n: others.length })}
          </summary>
          <div className="mt-2 space-y-2">
            {others.map((fix) => (
              <FixOption key={fix.id} fix={fix} result={result} previewId={previewId} onPreview={onPreview} onApply={onApply} dense={dense} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function FixOption({
  fix,
  result,
  previewId,
  onPreview,
  onApply,
  recommended = false,
  dense,
}: {
  fix: Fix;
  result: CompileResult;
  previewId: string | null;
  onPreview: (id: string | null) => void;
  onApply: (fix: Fix) => void;
  recommended?: boolean;
  dense: boolean;
}) {
  const { t, lang } = useI18n();
  const r = fix.replacement;
  const count = result.document.sizes.length;
  const sizes: SizeSet = fix.sizes.length >= count && count > 1 ? 'all' : fix.sizes;
  const changes = useMemo(
    () => (r ? summarizeChanges(r.old_text, r.new_text, result.document.sizes.map((s) => sizeName(result, s.index, lang))) : []),
    [r, result, lang],
  );
  const note = fixNote(fix, lang);
  const previewing = previewId === fix.id;
  return (
    <div
      className={`rounded-control ${recommended ? '' : 'border-t border-line pt-3'}`}
      data-testid="fix-card"
      data-recommended={recommended ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-center gap-2">
        {recommended && (
          <span className="rounded-pill bg-accent/[0.12] px-2 py-0.5 text-2xs font-bold text-accent-strong" title={t('fix.recommendedTitle')} data-testid="fix-recommended">
            {t('fix.recommended')}
          </span>
        )}
        <span className="text-xs font-semibold text-muted" data-testid="fix-kind">
          {t(`fix.kind.${fixKindCode(fix)}`)}
        </span>
        {sizes !== 'all' && count > 1 && <SizeBadges sizes={sizes} result={result} />}
        <span className="sr-only">{sizesLabel(sizes, result, t, lang)}</span>
        {dense && <span className="text-2xs text-subtle">{t('diag.cost', { n: fix.cost })}</span>}
        <Switch
          className="ml-auto"
          checked={previewing}
          onChange={(v) => onPreview(v ? fix.id : null)}
          label={recommended ? t('find.preview') : t('find.previewThis')}
          testId="fix-preview-toggle"
        />
      </div>
      <div className="mt-2 space-y-2">
        {r ? <ChangeSummary changes={changes} t={t} /> : <BeforeAfter before={fix.before} after={fix.after} t={t} />}
        {note.text && (
          <p className="text-sm text-muted" lang={note.lang !== lang ? note.lang : undefined}>
            {note.text}
          </p>
        )}
        {!r && <p className="text-xs italic text-subtle">{t('diag.applyDisabled')}</p>}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => onApply(fix)}
            disabled={!r}
            title={r ? t('diag.applyTitle', { n: r.line_no }) : t('diag.applyDisabled')}
            data-testid="apply-fix"
            className="btn-primary disabled:bg-fg/10 disabled:text-subtle disabled:opacity-100"
          >
            <Icon name="check" size={13} />
            {t('diag.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface AppliedFix {
  id: string;
  fix: Fix;
  /** Line number (in the text at the time of applying). */
  line: number;
  oldText: string;
  newText: string;
  summary: string;
}

/** Fixes applied from the notebook, each with its "Annuler". */
export function AppliedFixes({ items, onUndo, onDismiss }: { items: AppliedFix[]; onUndo: (a: AppliedFix) => void; onDismiss: (a: AppliedFix) => void }) {
  const { t } = useI18n();
  if (items.length === 0) return null;
  return (
    <section aria-label={t('find.appliedTitle')} className="space-y-2" data-testid="applied-fixes">
      {items.map((a) => (
        <div key={a.id} className="fl-fade-in flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card bg-accent/[0.07] px-4 py-3 text-sm" data-testid="applied-fix">
          <Icon name="check" size={14} className="text-accent-strong" />
          <span className="font-semibold text-accent-strong">{t('find.applied', { n: a.line })}</span>
          {a.summary && <span className="tabular text-muted">{a.summary}</span>}
          <span className="ml-auto flex items-center gap-1">
            <button type="button" className="btn-secondary h-7" onClick={() => onUndo(a)} data-testid="undo-fix">
              <Icon name="undo" size={13} />
              {t('find.undo')}
            </button>
            <button type="button" className="btn-quiet h-7 px-1.5" onClick={() => onDismiss(a)} aria-label={t('find.dismiss')} title={t('find.dismiss')}>
              <Icon name="x" size={12} />
            </button>
          </span>
        </div>
      ))}
    </section>
  );
}

/** Nothing was understood: the absence of diagnostics proves nothing (never a green state). */
export function NoneUnderstood() {
  const { t } = useI18n();
  return (
    <div className="rounded-card bg-surface px-6 py-8" data-testid="diagnostics-none-understood" role="status">
      <div className="flex items-start gap-3">
        <SeverityIcon severity="warning" size={20} className="mt-1" />
        <div>
          <p className="font-display text-xl font-[460] text-fg">{t('diag.noneTitle')}</p>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted">{t('diag.noneBody')}</p>
        </div>
      </div>
    </div>
  );
}

/** Every size adds up: a quiet row of stitches casting off, and the sentence. */
export function Celebration({ children }: { children?: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="rounded-card bg-surface px-6 py-8" data-testid="diagnostics-empty">
      <div className="flex items-start gap-3">
        <span className="relative mt-1 flex h-5 w-5 items-center justify-center rounded-pill bg-accent/15 text-accent-strong">
          <Icon name="check" size={12} />
          {[-10, 0, 10].map((x, i) => (
            <span
              key={x}
              className="fl-celebrate-dot absolute left-1/2 top-1/2 h-1 w-1 rounded-pill bg-accent"
              style={{ marginLeft: x, animationDelay: `${150 + i * 140}ms` }}
              aria-hidden="true"
            />
          ))}
        </span>
        <div>
          <p className="font-display text-xl font-[460] text-fg">{t('diag.empty')}</p>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted">{t('diag.emptyHint')}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
